import React from 'react';
import { Button, Dropdown, MenuProps, Tag } from 'antd';
import { EllipsisOutlined } from '@ant-design/icons';
import { TFunction } from 'i18next';
import getDateWithTimeZone, { dateFormatCase } from 'helpers/getDateWithTimeZone';
import dayjs from 'dayjs';
import { IPaymentHistoryItem } from 'interfaces';


interface GetColumnsProps {
  t: TFunction;
  dateFormat: keyof typeof dateFormatCase;
  roomFilters: { text: string; value: string }[];
  cardFilters: { text: string; value: string }[];
  onOpenRefundModal: (record: IPaymentHistoryItem) => void;
}

export const getPaymentHistoryColumns = ({
  t,
  dateFormat,
  roomFilters,
  cardFilters,
  onOpenRefundModal,
}: GetColumnsProps) => {
  const statusFilters = [
    { text: t('Billing.status.paid'), value: 'paid' },
    { text: t('Billing.status.error'), value: 'error' },
    { text: t('Billing.status.refund'), value: 'refund' },
  ];

  const getStatusTag = (status: string) => {
    switch (status) {
      case 'paid':
        return <Tag color="success">{t('Billing.status.paid')}</Tag>;
      case 'error':
        return <Tag color="error">{t('Billing.status.error')}</Tag>;
      case 'refund':
        return <Tag color="default">{t('Billing.status.refund')}</Tag>;
      case 'declined':
        return <Tag color="error">{t('Billing.columns.declined')}</Tag>;
      case 'awaiting_payment':
        return <Tag color="warning">{t('Billing.columns.awaiting_payment')}</Tag>;
      default:
        return <Tag color="default">{status || '——'}</Tag>;
    }
  };

  return [
    {
      title: t('Billing.historyColumns.id'),
      dataIndex: 'id',
      key: 'id',
      width: '12%',
    },
    {
      title: t('Billing.historyColumns.created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: '16%',
      sorter: (a: IPaymentHistoryItem, b: IPaymentHistoryItem) =>
        dayjs(a.created_at).valueOf() - dayjs(b.created_at).valueOf(),
      render: (val: string) => (val ? getDateWithTimeZone(val, dateFormat) : '-'),
    },
    {
      title: t('Billing.historyColumns.room_name'),
      dataIndex: 'room_name',
      key: 'room_name',
      width: '18%',
      filters: roomFilters,
      onFilter: (value: any, record: IPaymentHistoryItem) => record.room_name === value,
    },
    {
      title: t('Billing.historyColumns.cost'),
      dataIndex: 'cost',
      key: 'cost',
      width: '14%',
      sorter: (a: IPaymentHistoryItem, b: IPaymentHistoryItem) => (a.cost || 0) - (b.cost || 0),
      render: (val: number) => (val !== undefined ? `${val.toLocaleString('ru-RU')} ₽` : '-'),
    },
    {
      title: t('Billing.historyColumns.payment_card'),
      dataIndex: 'payment_card',
      key: 'payment_card',
      width: '18%',
      filters: cardFilters,
      onFilter: (value: any, record: IPaymentHistoryItem) => record.payment_card === value,
    },
    {
      title: t('Billing.historyColumns.payment_status'),
      dataIndex: 'payment_status',
      key: 'payment_status',
      width: '12%',
      filters: statusFilters,
      onFilter: (value: any, record: IPaymentHistoryItem) => record.payment_status === value,
      render: (val: string) => getStatusTag(val),
    },
    {
      title: t('Billing.historyColumns.actions'),
      dataIndex: 'actions',
      key: 'actions',
      width: '10%',
      render: (_: any, record: IPaymentHistoryItem) => {
        const isRefundable = record.payment_status === 'paid';
        const menuItems: MenuProps['items'] = [
          {
            key: 'refund',
            label: t('Billing.actions.refund'),
            disabled: !isRefundable,
            onClick: () => onOpenRefundModal(record),
          },
        ];

        return (
          <Dropdown menu={{ items: menuItems }} trigger={['click']}>
            <Button type="text" icon={<EllipsisOutlined />} />
          </Dropdown>
        );
      },
    },
  ];
};
